import React, { useMemo } from 'react';

import type { BoardLocalState } from 'src/components/workflow/board/Board';
import { PriorityDisplay } from 'src/components/workflow/workflow-list/cells';
import { FilterPopoverContent } from 'src/components/workflow/workflow-list/filter-contents/FilterPopoverContent';
import { Apollo } from 'src/gql/generated';
import type { ReactSetStateTuple } from 'src/types';

function getIsWorkflowPriority(value: string): value is Apollo.WorkflowPriority {
  return Object.values(Apollo.WorkflowPriority).some(v => v === value);
}

const PRIORITIES = [
  { id: Apollo.WorkflowPriority.Urgent, label: 'Urgent' },
  { id: Apollo.WorkflowPriority.High, label: 'High' },
  { id: Apollo.WorkflowPriority.Medium, label: 'Medium' },
  { id: Apollo.WorkflowPriority.Low, label: 'Low' },
];

interface BoardPriorityFilterContentProps {
  boardStateTuple: ReactSetStateTuple<BoardLocalState>;
}

export function BoardPriorityFilterContent({
  boardStateTuple,
}: BoardPriorityFilterContentProps): JSX.Element {
  const [boardState, setBoardState] = boardStateTuple;

  const selectedIds = useMemo(
    () => new Set(boardState.filterPriorities?.map(String) ?? []),
    [boardState.filterPriorities]
  );

  const handleToggle = (priorityId: string) => {
    const isSelected = selectedIds.has(priorityId);

    if (isSelected) {
      const newPriorities = (boardState.filterPriorities ?? []).filter(
        p => String(p) !== priorityId
      );
      setBoardState(s => ({ ...s, filterPriorities: newPriorities }));
      return;
    }

    if (!getIsWorkflowPriority(priorityId)) return;

    setBoardState(s => ({
      ...s,
      filterPriorities: [...(s.filterPriorities ?? []), priorityId],
    }));
  };

  return (
    <FilterPopoverContent
      options={PRIORITIES}
      renderOption={option => <PriorityDisplay priority={option.id} />}
      selectedIds={selectedIds}
      onToggle={handleToggle}
    />
  );
}
